import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Files, General, MediaDevices } from 'icons/untitledui';
import {
  isDocumentFile,
  isVideoFile,
} from 'shiftNotes/helpers/noteCaptureAttachmentLimits';

import * as Styled from 'shiftNotes/styles/NoteCaptureAttachmentsPreview.style';

export interface NoteCaptureAttachmentsPreviewProps {
  files: File[];
  onRemove: (file: File) => void;
}

interface Preview {
  file: File;
  url: string;
}

export default function NoteCaptureAttachmentsPreview({
  files,
  onRemove,
}: NoteCaptureAttachmentsPreviewProps): JSX.Element | null {
  const { t } = useTranslation('pageShiftNotes');
  const [previews, setPreviews] = useState<Preview[]>([]);

  useEffect(() => {
    const next = files.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setPreviews(next);

    return () => {
      next.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, [files]);

  if (previews.length === 0) return null;

  const renderPreview = ({ file, url }: Preview) => {
    if (isDocumentFile(file)) {
      return (
        <Styled.DocumentThumbnail>
          <Files.File02 aria-hidden />
          <Styled.DocumentName title={file.name}>
            {file.name}
          </Styled.DocumentName>
        </Styled.DocumentThumbnail>
      );
    }
    if (isVideoFile(file)) {
      return (
        <Styled.VideoThumbnail>
          <MediaDevices.VideoRecorder />
          <Styled.VideoLabel>
            {t('noteCaptureAttachments.videoLabel')}
          </Styled.VideoLabel>
        </Styled.VideoThumbnail>
      );
    }
    return (
      <Styled.Image
        src={url}
        alt={t('noteCaptureAttachments.photoPreviewAlt')}
      />
    );
  };

  return (
    <Styled.Container data-testid="note-capture-attachments-preview">
      {previews.map((preview) => (
        <Styled.Item
          key={preview.url}
          $isDocument={isDocumentFile(preview.file)}
        >
          {renderPreview(preview)}
          <Styled.RemoveButton
            type="button"
            onClick={() => onRemove(preview.file)}
            aria-label={t('noteCaptureAttachments.removeAttachment')}
          >
            <General.XClose />
          </Styled.RemoveButton>
        </Styled.Item>
      ))}
    </Styled.Container>
  );
}
